import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/auth/AuthContext';
import { stallsApi } from '@/api/stalls';
import { mediaApi, isAllowedPhotoType, type AllowedPhotoType } from '@/api/media';
import { ApiHttpError, ApiNetworkError } from '@/api/client';
import { GeoError, getCurrentLocation } from '@/hooks/useGeofence';

type Step = 1 | 2 | 3;

interface FormState {
  name: string;
  category: string;
  dishes: string;
  address: string;
  openTime: string;
  closeTime: string;
}

interface PinState {
  lat: number | null;
  lng: number | null;
  loading: boolean;
  error: string | null;
}

const CATEGORIES = [
  'Wok',
  'Noodle',
  'Grill',
  'Som tam · Isan',
  'Sweets',
  'Drink cart',
  'Rice plate',
  'Late-night',
];

const thaiRe = /[฀-๿]/;
// Split "EN · TH" or single-script input into ({name_th, name_en?}). The
// backend requires a non-empty name_th, so if the user gives only a Latin
// name we reuse it in both slots (the curator will fix the Thai script).
function splitName(raw: string): { name_th: string; name_en?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { name_th: '' };
  if (trimmed.includes('·')) {
    const [a = '', b = ''] = trimmed.split('·').map((s) => s.trim());
    const aIsTh = thaiRe.test(a);
    const bIsTh = thaiRe.test(b);
    if (aIsTh && !bIsTh) return { name_th: a, name_en: b || undefined };
    if (bIsTh && !aIsTh) return { name_th: b, name_en: a || undefined };
    // Both same script — keep both halves in original order.
    return { name_th: a, name_en: b || a };
  }
  return thaiRe.test(trimmed) ? { name_th: trimmed } : { name_th: trimmed, name_en: trimmed };
}

function splitDishes(raw: string): string[] {
  return raw
    .split(/[,·]/)
    .map((d) => d.trim())
    .filter(Boolean);
}

export function AddRoute() {
  const { t } = useTranslation();
  const toast = useToast();
  const nav = useNavigate();
  const auth = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>({
    name: '',
    category: CATEGORIES[0] ?? '',
    dishes: '',
    address: '',
    openTime: '10:30',
    closeTime: '20:00',
  });
  const [pin, setPin] = useState<PinState>({
    lat: null,
    lng: null,
    loading: false,
    error: null,
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Resolve a pin coordinate when the user enters the location step. Done lazily
  // so the geolocation prompt fires from a user gesture (step transition) and
  // not on first paint.
  useEffect(() => {
    if (step !== 2 || pin.lat !== null || pin.loading) return;
    setPin((s) => ({ ...s, loading: true, error: null }));
    getCurrentLocation()
      .then((loc) =>
        setPin({ lat: loc.lat, lng: loc.lng, loading: false, error: null }),
      )
      .catch((e: unknown) => {
        const msg =
          e instanceof GeoError
            ? e.kind === 'denied'
              ? t('add.geo_denied')
              : e.kind === 'timeout'
                ? t('add.geo_timeout')
                : t('add.geo_unavailable')
            : t('add.geo_unavailable');
        setPin({ lat: null, lng: null, loading: false, error: msg });
      });
  }, [step, pin.lat, pin.loading, t]);

  // Revoke the preview blob URL when it changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const isLast = step === 3;
  const nextLabel = useMemo(() => {
    if (step === 1) return t('add.next_to_location');
    if (step === 2) return t('add.next_to_photo');
    return submitting ? t('add.submitting') : t('add.submit');
  }, [step, t, submitting]);

  const canAdvance = (): boolean => {
    if (step === 1) return form.name.trim().length > 0;
    if (step === 2) return pin.lat !== null && pin.lng !== null;
    return !!photoFile;
  };

  const handlePhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAllowedPhotoType(file.type)) {
      toast.show(t('add.photo_type_error'));
      e.target.value = '';
      return;
    }
    // Size cap is enforced authoritatively by mediaApi.uploadPhoto against
    // signed.max_bytes. We don't pre-check here so a backend cap change
    // doesn't require a re-deploy of the SPA.
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const submit = async (origin: HTMLButtonElement) => {
    if (submitting) return;
    if (!auth.user) {
      toast.show(t('add.sign_in_required'));
      return;
    }
    if (!photoFile) {
      toast.show(t('add.photo_required'));
      return;
    }
    if (pin.lat === null || pin.lng === null) {
      toast.show(t('add.pin_required'));
      return;
    }
    const token = await auth.getToken();
    if (!token) {
      toast.show(t('add.sign_in_required'));
      return;
    }
    setSubmitting(true);
    try {
      const contentType = photoFile.type as AllowedPhotoType;
      const signed = await mediaApi.requestUploadUrl(contentType, token);
      const uploaded = await mediaApi.uploadPhoto(photoFile, signed);
      const { name_th, name_en } = splitName(form.name);
      const res = await stallsApi.submit(
        {
          name_th,
          name_en,
          category: form.category || undefined,
          dishes: splitDishes(form.dishes),
          lat: pin.lat,
          lng: pin.lng,
          address: form.address.trim() || undefined,
          photo_media_key: uploaded.object_key,
        },
        token,
      );
      const points = 50;
      toast.burst(origin, points);
      toast.show(
        res.auto_published ? t('add.success_published') : t('add.success_pending'),
        points,
      );
      setTimeout(
        () => nav(res.auto_published ? `/stall/${res.stall_id}` : '/profile'),
        1100,
      );
    } catch (e) {
      const msg =
        e instanceof ApiHttpError
          ? e.message
          : e instanceof ApiNetworkError
            ? t('add.network_error')
            : e instanceof Error
              ? e.message
              : t('add.network_error');
      toast.show(msg);
      setSubmitting(false);
    }
  };

  const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!canAdvance()) {
      if (step === 1) toast.show(t('add.name_required'));
      else if (step === 2) toast.show(t('add.pin_required'));
      else toast.show(t('add.photo_required'));
      return;
    }
    if (!isLast) {
      setStep(((step + 1) as Step));
      return;
    }
    void submit(e.currentTarget);
  };
  const onBack = () => {
    if (step > 1) setStep(((step - 1) as Step));
  };

  const upd = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="mx-auto max-w-[600px] px-4 pb-16 pt-4">
      <h1 className="h1 m-0">{t('add.title')}</h1>
      <p className="muted m-0 text-sm">{t('add.note_queue')}</p>

      <nav className="my-6 flex items-center gap-1.5" aria-label="Progress">
        {([1, 2, 3] as const).map((n) => {
          const labelKey =
            n === 1 ? 'add.step_basics' : n === 2 ? 'add.step_location' : 'add.step_photo';
          const state = step === n ? 'active' : step > n ? 'done' : 'pending';
          return (
            <div key={n} className="flex flex-1 flex-col gap-1 text-xs font-semibold">
              <span
                className="h-1 rounded transition-colors"
                style={{
                  background:
                    state === 'active'
                      ? 'var(--soi-600)'
                      : state === 'done'
                        ? 'var(--soi-400)'
                        : 'var(--border)',
                }}
              />
              <span
                style={{
                  color:
                    state === 'active'
                      ? 'var(--soi-700)'
                      : state === 'done'
                        ? 'var(--ink-soft)'
                        : 'var(--fg-muted)',
                }}
              >
                {t(labelKey)}
              </span>
            </div>
          );
        })}
      </nav>

      {step === 1 && (
        <section className="space-y-3">
          <Field id="name" label={t('add.field_name')} helper={t('add.field_name_helper')}>
            <input
              id="name"
              value={form.name}
              onChange={(e) => upd('name', e.target.value)}
              placeholder="e.g. Auntie Som's som tam · ป้าส้มส้มตำ"
              className="field-input"
            />
          </Field>
          <Field id="category" label={t('add.field_category')}>
            <select
              id="category"
              value={form.category}
              onChange={(e) => upd('category', e.target.value)}
              className="field-input"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field id="dishes" label={t('add.field_dishes')} helper={t('add.field_dishes_helper')}>
            <input
              id="dishes"
              value={form.dishes}
              onChange={(e) => upd('dishes', e.target.value)}
              placeholder="e.g. Pad Thai, Pad See Ew"
              className="field-input"
            />
          </Field>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-3">
          <Field
            id="address"
            label={t('add.field_address')}
            helper={t('add.field_address_helper')}
          >
            <input
              id="address"
              value={form.address}
              onChange={(e) => upd('address', e.target.value)}
              placeholder="Soi Ari 1"
              className="field-input"
            />
          </Field>
          <div
            className="grid aspect-[16/10] place-items-center rounded-lg"
            style={{
              background: 'linear-gradient(135deg, #d8eaef, #e3edd6)',
              backgroundImage:
                'radial-gradient(rgba(15, 76, 92, 0.08) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
            aria-label="Pin selector"
            role="img"
          >
            <div style={{ position: 'relative', width: 28, height: 28 }}>
              <div className="pin open" style={{ transform: 'rotate(-45deg) scale(1.6)' }} />
            </div>
          </div>
          <div className="text-center text-[12px] text-ink-muted">
            {pin.loading
              ? t('add.pin_locating')
              : pin.error
                ? pin.error
                : pin.lat !== null && pin.lng !== null
                  ? t('add.pin_set', { lat: pin.lat.toFixed(5), lng: pin.lng.toFixed(5) })
                  : t('add.pin_waiting')}
          </div>
          <Field id="hours" label={t('add.field_hours')}>
            <div className="grid grid-cols-[100px_1fr_1fr] items-center gap-2">
              <span className="text-sm text-ink-muted">Open</span>
              <input
                type="time"
                value={form.openTime}
                onChange={(e) => upd('openTime', e.target.value)}
                className="field-input"
              />
              <input
                type="time"
                value={form.closeTime}
                onChange={(e) => upd('closeTime', e.target.value)}
                className="field-input"
              />
            </div>
          </Field>
        </section>
      )}

      {step === 3 && (
        <section>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoPick}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="grid w-full aspect-[16/10] place-items-center overflow-hidden rounded-lg border-2 border-dashed border-[color:var(--border)] p-6 text-center text-ink-muted transition hover:border-soi-400 hover:bg-soi-50"
          >
            {photoPreview ? (
              <img
                src={photoPreview}
                alt={t('add.photo_preview_alt')}
                className="h-full w-full object-cover"
              />
            ) : (
              <div>
                <div className="mb-2 text-5xl" aria-hidden="true">
                  📷
                </div>
                <p className="m-0 font-bold">{t('add.photo_title')}</p>
                <p className="mt-1 text-[13px]">{t('add.photo_body')}</p>
              </div>
            )}
          </button>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-pill bg-soi-100 px-3 py-1 text-xs font-semibold text-soi-700">
              {form.category}
            </span>
            <span className="rounded-pill bg-cream px-3 py-1 text-xs font-semibold text-ink-soft">
              {form.openTime} – {form.closeTime}
            </span>
          </div>
          <article className="mt-4 rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-alt)] p-3 text-sm">
            <strong>What happens next</strong>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-soft">
              <li>Curators review within 24 hours.</li>
              <li>You get a notification when approved · +50 points.</li>
              <li>If we have questions, we&apos;ll comment on your submission.</li>
            </ul>
          </article>
        </section>
      )}

      <div className="mt-6 flex items-center justify-between gap-2 border-t border-[color:var(--border)] pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={step === 1 || submitting}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-ink-soft disabled:opacity-40"
        >
          {t('add.back')}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={submitting}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-soi-600 px-4 py-2 text-sm font-semibold text-white hover:bg-soi-700 disabled:opacity-60"
        >
          {nextLabel}
        </button>
      </div>
      <style>
        {`
          .field-input {
            min-height: 44px;
            font: inherit;
            padding: 12px 14px;
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: var(--radius-md);
            color: var(--fg);
            width: 100%;
            outline: none;
            transition: border-color var(--t-fast) var(--ease-out),
              box-shadow var(--t-fast) var(--ease-out);
          }
          .field-input:focus {
            border-color: var(--soi-600);
            box-shadow: 0 0 0 3px color-mix(in srgb, var(--soi-600) 22%, transparent);
          }
        `}
      </style>
    </div>
  );
}

function Field({
  id,
  label,
  helper,
  children,
}: {
  id: string;
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-ink-soft">
        {label}
      </label>
      {children}
      {helper && <span className="text-[12px] text-ink-muted">{helper}</span>}
    </div>
  );
}
